'use client';

import type { PropsWithChildren, ReactElement, ReactNode } from 'react';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { usePrevious } from 'hooks/usePrevious';
import type { Key } from '@react-types/shared';
import type { AnimationVariants } from './AnimateItemContext';
import { AnimateItemProvider } from './AnimateItemContext';
import { useAnimateMutationContext } from './AnimateMutationProvider';

type Props = PropsWithChildren<{
  disabled?: boolean;
}>;

/**
 * Largely inspired by the technique created by framer-motion for
 * AnimatePresence, but with a few key differences:
 *
 * - We can rely on `layoutInfo` to determine if a node is entering
 *   or exiting.
 * - We can conditionally disable most of the logic if animations
 *   are disabled, which they are the vast majority of time.
 */

export function AnimateItemGroup({
  disabled = false,
  children,
}: Props): React.ReactElement<any> {
  const { animating, layoutInfo, isFullyMeasured } =
    useAnimateMutationContext();
  const previousLayout = usePrevious(layoutInfo);
  const enabled = !disabled && animating;

  const previousChildrenWithoutContext = useRef<ReactNode[]>([]);
  const childrenWithoutContext: Array<ReactNode> = [];

  const childNodes: ReactElement<any>[] = [];
  const currentNodeSet = new Set();
  const previousNodeSet = useRef(new Set());

  React.Children.forEach(children, (child) => {
    const key = getChildKey(child);

    childrenWithoutContext.push(child);
    currentNodeSet.add(key);

    const previousNode = previousLayout?.get(key);

    // @ts-expect-error FIXME(strictNullChecks)
    let animation: AnimationVariants = null;
    // @ts-expect-error FIXME(strictNullChecks)
    let startY: number = null;
    // @ts-expect-error FIXME(strictNullChecks)
    let startX: number = null;

    if (enabled) {
      const parentKey = layoutInfo.get(key)?.parentKey;
      // @ts-expect-error FIXME(strictNullChecks)
      const prevParent = previousLayout?.get(parentKey);
      const isParentMounting = parentKey && !prevParent;

      if (!isParentMounting) {
        if (!previousNode) {
          animation = 'mount-mutation';
        } else if (!previousNodeSet.current?.has(key)) {
          animation = 'mount-virtualization';
          startY = previousNode.rect.y - (prevParent?.rect.y ?? 0);
          startX = previousNode.rect.x - (prevParent?.rect.x ?? 0);
        }
      }
    }

    childNodes.push(
      <AnimateItemProvider
        key={key}
        animated={enabled}
        isFullyMeasured={isFullyMeasured}
        startX={startX}
        startY={startY}
        type={animation}
      >
        {child}
      </AnimateItemProvider>
    );
  });

  useLayoutEffect(() => {
    previousNodeSet.current = currentNodeSet;
    previousChildrenWithoutContext.current = childrenWithoutContext;
  });

  const childrenToRender: ReactElement<any>[] = [...childNodes];

  const exitingChildren = useRef(new Map()).current;

  useEffect(() => {
    if (!enabled) {
      if (exitingChildren.size > 0) {
        exitingChildren.clear();
      }
    }
  }, [enabled, exitingChildren]);

  // If animations are disabled or its the initial mount,
  // return early because nothing is exiting.
  if (!enabled || !previousLayout) {
    return childrenToRender as any;
  }

  previousChildrenWithoutContext.current.forEach((child) => {
    const key = getChildKey(child);
    if (!currentNodeSet.has(key) && !exitingChildren.has(key)) {
      exitingChildren.set(key, child);
    }
  });

  exitingChildren.forEach((child, key) => {
    if (currentNodeSet.has(key)) {
      exitingChildren.delete(key);
      return;
    }

    const insertionIndex = previousChildrenWithoutContext.current.findIndex(
      (current) => getChildKey(current) === key
    );

    let animation: AnimationVariants = 'exit-mutation';
    // @ts-expect-error FIXME(strictNullChecks)
    let exitY: number = null;
    // @ts-expect-error FIXME(strictNullChecks)
    let exitX: number = null;

    if (layoutInfo.has(key)) {
      const info = layoutInfo.get(key);
      // @ts-expect-error FIXME(strictNullChecks)
      const parent = layoutInfo.get(info.parentKey);
      animation = 'exit-virtualization';
      // @ts-expect-error FIXME(strictNullChecks)
      exitY = info.rect.y - (parent?.rect.y ?? 0);
      // @ts-expect-error FIXME(strictNullChecks)
      exitX = info.rect.x - (parent?.rect.x ?? 0);
    }

    childrenToRender.splice(
      insertionIndex,
      0,
      <AnimateItemProvider
        key={child.key}
        animated
        exitX={exitX}
        exitY={exitY}
        isFullyMeasured={isFullyMeasured}
        type={animation}
      >
        {child}
      </AnimateItemProvider>
    );
  });

  return childrenToRender as any;
}

function getChildKey(node: any): Key {
  return node.key!;
}
